import React, { useEffect, useState } from "react";
import { AuthBranding } from "components/common/branding";
import { ErrorBoundaryPage } from "components/common/error/ErrorBoundaryPage";
import {
  AuthContainer,
  FormContainer,
  Tabs,
} from "components/common/container";
import { StyledLabel, StyledText } from "components/common/info/styled";
import { Error, ErrorContainer, Input } from "components/common/inputs/styled";
import styled from "styled-components";
import { MedButton } from "components/common/buttons";
import validateJWT from "utils/validateJWT";
import axios from "axios";
import { extractAxiosErrorMessage } from "utils/AxiosError";
import { useKeycloak } from "@react-keycloak/web";
import keycloak from "authentication/keycloak";
import { ROUTES } from "utils/constants/routes-constants";
import { JwtValidationErrors } from "utils/JwtValidationErrors";
import { LoaderOverlay } from "components/common/loaders";

const apiBaseUrl = process.env.REACT_APP_URL_API;

const StyledInput = styled(Input)`
  margin-top: 10px;
  margin-bottom: 30px;
`;

const StyledButton = styled(MedButton)`
  margin-bottom: 20px;
`;

const StyledErrorContainer = styled(ErrorContainer)`
  margin-top: -30px;
  margin-bottom: 20px;
`;

const UnauthorisedMessage = styled(StyledText)`
  margin-top: 30px;
  color: ${(props) => props.theme.colors.secondaryCoral};
  font-size: 24px;
`;

const StyledListItem = styled.li`
  margin-left: 10px;
`;

export const ResetPassword: React.FC = () => {
  const [passwordRequirementErrors, setPasswordRequirementErrors] = useState<
    string[]
  >([]);
  const [passwordMismatchError, setPasswordMismatchError] = useState<
    string | null
  >(null);
  const [error, setError] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isValidating, setIsValidating] = useState<boolean>(true);
  const { initialized } = useKeycloak();
  const { search } = window.location;
  const emailVerifiedToken = new URLSearchParams(search);
  const jwtToken = emailVerifiedToken.get("token") || "";
  const invalidTokenText =
    "Invalid token, please start the reset process again";

  useEffect(() => {
    if (jwtToken && jwtToken !== "Window") {
      const validatedToken = validateJWT(jwtToken);
      if (validatedToken) {
        if (validatedToken.isValidToken) {
          setEmail(validatedToken.sub || "");
          setIsValidating(false);
        } else {
          setError(true);
          setIsValidating(false);

          switch (validatedToken.error) {
            case JwtValidationErrors.TokenExpired:
              setErrorMsg(
                "The password reset link has expired, please start the reset process again"
              );
              break;
            case JwtValidationErrors.InvalidToken:
              setErrorMsg(invalidTokenText);
              break;
            default:
              setErrorMsg(invalidTokenText);
              break;
          }
        }
      } else {
        setError(true);
        setErrorMsg(invalidTokenText);
        setIsValidating(false);
      }
    } else {
      setError(true);
      setErrorMsg("You are not authorised to view this page");
      setIsValidating(false);
    }
  }, [initialized]);

  const checkPasswordRequirements = (password: string): string[] => {
    const errors: string[] = [];

    if (password.length === 0) {
      return errors;
    }

    if (password.length < 8) {
      errors.push("at least 8 characters");
    }
    if (!/(?=.*[a-z])/.test(password)) {
      errors.push("at least 1 lower-case letter");
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push("at least 1 upper-case letter");
    }
    if (!/(?=.*\d)/.test(password)) {
      errors.push("at least 1 number");
    }
    if (!/(?=.*[@$!%*?&])/.test(password)) {
      errors.push("at least 1 special character");
    }

    return errors;
  };

  const handleSubmit = async () => {
    setError(false);
    setErrorMsg("");

    const requirementErrors = checkPasswordRequirements(password);
    setPasswordRequirementErrors(requirementErrors);

    if (requirementErrors.length > 0) {
      setPasswordMismatchError(null);
      return;
    }

    if (password.length === 0) {
      setPasswordMismatchError("Password cannot be empty");
      return;
    }

    if (password !== confirmPassword) {
      setPasswordMismatchError("Passwords must match");
      return;
    } else {
      setPasswordMismatchError(null);
    }

    const payload = {
      email: email,
      password: password,
    };

    await axios
      .post(`${apiBaseUrl}/v1/updatepassword`, payload, {
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + jwtToken,
        },
      })
      .then(() => {
        keycloak.login({
          loginHint: email,
          redirectUri: `${window.location.origin}${ROUTES.HOME}`,
        });
      })
      .catch((axiosError) => {
        setError(true);
        setErrorMsg(
          extractAxiosErrorMessage(
            axiosError,
            "An error occurred while resetting credentials. Please try again later."
          )
        );
      });
  };

  if (isValidating) {
    return (
      <ErrorBoundaryPage>
        <LoaderOverlay />
      </ErrorBoundaryPage>
    );
  }

  if (error) {
    return (
      <ErrorBoundaryPage>
        <AuthBranding />
        <AuthContainer>
          <Tabs
            tabs={[
              {
                name: "user",
                text: "Create/Reset Password",
                isActive: true,
              },
            ]}
          />
          <FormContainer>
            <UnauthorisedMessage>{errorMsg}</UnauthorisedMessage>
            <StyledText>
              <a href="/">Back</a>
            </StyledText>
          </FormContainer>
        </AuthContainer>
      </ErrorBoundaryPage>
    );
  }

  return (
    <ErrorBoundaryPage>
      <AuthBranding />
      <AuthContainer>
        <Tabs
          tabs={[
            {
              name: "user",
              text: "Create/Reset Password",
              isActive: true,
            },
          ]}
        />
        <FormContainer>
          <StyledLabel>Email</StyledLabel>
          <StyledInput type="text" value={email} disabled={true} />
          <StyledLabel>Password</StyledLabel>
          <StyledInput
            type="password"
            placeholder="Enter password"
            onChange={(e) => {
              const newPassword = e.target.value;
              setPassword(newPassword);
              setPasswordRequirementErrors(
                checkPasswordRequirements(newPassword)
              );
              setPasswordMismatchError(null);
            }}
          />

          <StyledLabel>Confirm Password</StyledLabel>
          <StyledInput
            type="password"
            placeholder="Confirm password"
            onChange={(value) => {
              setConfirmPassword(value.target.value);
            }}
          />
          {(passwordRequirementErrors.length > 0 || passwordMismatchError) && (
            <StyledErrorContainer>
              <Error $red>
                {passwordMismatchError ? (
                  passwordMismatchError
                ) : (
                  <>
                    <p>Passwords must have:</p>
                    <ul>
                      {passwordRequirementErrors.map((msg, index) => (
                        <StyledListItem key={index}>{msg}</StyledListItem>
                      ))}
                    </ul>
                  </>
                )}
              </Error>
            </StyledErrorContainer>
          )}
          <StyledButton $coral width={23} onClick={handleSubmit}>
            Submit
          </StyledButton>
          <StyledText>
            After setting your password, you will be redirected to the login
            page to login with the new password.
          </StyledText>
          <StyledText>
            <a href="/">Back</a>
          </StyledText>
        </FormContainer>
      </AuthContainer>
    </ErrorBoundaryPage>
  );
};
